// =============================================================================
// Formatted Input Components
// Auto-format percentages (X.XX%) and currency ($X,XXX.XX) on blur
// =============================================================================

import { useState, useEffect } from 'react';

// =============================================================================
// Percent Input - shows "X.XX%" on blur, raw number while editing
// =============================================================================

interface PercentInputProps {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
  decimals?: number;
}

export function PercentInput({
  value,
  onChange,
  min,
  max,
  placeholder,
  className = '',
  readOnly = false,
  decimals = 2,
}: PercentInputProps) {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      if (value !== null && value !== undefined && value !== 0) {
        setDisplayValue(value.toFixed(decimals) + '%');
      } else if (value === 0) {
        setDisplayValue('0.' + '0'.repeat(decimals) + '%');
      } else {
        setDisplayValue('');
      }
    }
  }, [value, isFocused, decimals]);

  const handleFocus = () => {
    setIsFocused(true);
    if (value !== null && value !== undefined) {
      // Show clean number without trailing zeros for editing
      setDisplayValue(String(value));
    } else {
      setDisplayValue('');
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const cleaned = displayValue.replace(/%/g, '').trim();
    const parsed = parseFloat(cleaned);
    if (isNaN(parsed) || cleaned === '') {
      onChange(null);
      setDisplayValue('');
    } else {
      let clamped = parsed;
      if (min !== undefined) clamped = Math.max(min, clamped);
      if (max !== undefined) clamped = Math.min(max, clamped);
      onChange(clamped);
      setDisplayValue(clamped.toFixed(decimals) + '%');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow digits, decimal point, minus, and percent sign
    if (/^-?\d*\.?\d*%?$/.test(raw) || raw === '') {
      setDisplayValue(raw);
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
      readOnly={readOnly}
    />
  );
}

// =============================================================================
// Currency Input - shows "$X,XXX.XX" on blur, raw number while editing
// =============================================================================

interface CurrencyInputProps {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  min?: number;
  placeholder?: string;
  className?: string;
}

export function CurrencyInput({
  value,
  onChange,
  min,
  placeholder,
  className = '',
}: CurrencyInputProps) {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      if (value !== null && value !== undefined) {
        setDisplayValue(
          '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        );
      } else {
        setDisplayValue('');
      }
    }
  }, [value, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    if (value !== null && value !== undefined) {
      setDisplayValue(String(value));
    } else {
      setDisplayValue('');
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const cleaned = displayValue.replace(/[$,]/g, '').trim();
    const parsed = parseFloat(cleaned);
    if (isNaN(parsed) || cleaned === '') {
      onChange(null);
      setDisplayValue('');
    } else {
      let clamped = parsed;
      if (min !== undefined) clamped = Math.max(min, clamped);
      onChange(clamped);
      setDisplayValue(
        '$' + clamped.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow digits, decimal, commas, dollar sign
    if (/^[$]?\d*[,\d]*\.?\d*$/.test(raw) || raw === '') {
      setDisplayValue(raw);
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
    />
  );
}

// =============================================================================
// Integer Input - allows free typing, no leading zeros
// =============================================================================

interface IntegerInputProps {
  value: number | null | undefined;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  className?: string;
}

export function IntegerInput({
  value,
  onChange,
  min = 0,
  max,
  className = '',
}: IntegerInputProps) {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      if (value !== null && value !== undefined) {
        setDisplayValue(String(value));
      } else {
        setDisplayValue('');
      }
    }
  }, [value, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    if (value !== null && value !== undefined) {
      setDisplayValue(String(value));
    } else {
      setDisplayValue('');
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const parsed = parseInt(displayValue, 10);
    if (isNaN(parsed) || displayValue === '') {
      onChange(min);
      setDisplayValue(String(min));
    } else {
      let clamped = parsed;
      if (min !== undefined) clamped = Math.max(min, clamped);
      if (max !== undefined) clamped = Math.min(max, clamped);
      onChange(clamped);
      setDisplayValue(String(clamped));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Only allow digits, allow empty for clearing
    if (/^\d*$/.test(raw)) {
      setDisplayValue(raw);
      // Live update parent (strip leading zeros)
      const parsed = parseInt(raw, 10);
      if (!isNaN(parsed)) {
        let clamped = parsed;
        if (max !== undefined) clamped = Math.min(max, clamped);
        onChange(clamped);
      }
    }
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={className}
    />
  );
}
